import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { ArticleListQuery, ProductType } from "@jipraks/shared";
import { api } from "@client/lib/api-client";
import { isNotFound, unwrap } from "@client/lib/api-result";

// A 404 is an answer, not a transient failure: never retry it.
const retryUnlessNotFound = (failureCount: number, error: unknown) =>
  !isNotFound(error) && failureCount < 2;

export function useProducts(type?: ProductType) {
  return useQuery({
    queryKey: ["products", type ?? "all"],
    queryFn: () => unwrap(api.api.products.$get({ query: type ? { type } : {} })),
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ["product", slug],
    queryFn: () => unwrap(api.api.products[":slug"].$get({ param: { slug } })),
    retry: retryUnlessNotFound,
  });
}

export function useArticles(query: Partial<ArticleListQuery>) {
  const params = {
    page: String(query.page ?? 1),
    ...(query.search ? { search: query.search } : {}),
    ...(query.tag ? { tag: query.tag } : {}),
  };
  return useQuery({
    queryKey: ["articles", params],
    queryFn: () => unwrap(api.api.articles.$get({ query: params })),
    placeholderData: keepPreviousData,
  });
}

export function useLatestArticles() {
  return useQuery({
    queryKey: ["articles", "latest"],
    queryFn: () => unwrap(api.api.articles.latest.$get()),
  });
}

export function useArticle(slug: string) {
  return useQuery({
    queryKey: ["article", slug],
    queryFn: () => unwrap(api.api.articles[":slug"].$get({ param: { slug } })),
    retry: retryUnlessNotFound,
  });
}

export function useTopic(slug: string) {
  return useQuery({
    queryKey: ["topic", slug],
    queryFn: () => unwrap(api.api.topics[":slug"].$get({ param: { slug } })),
    retry: retryUnlessNotFound,
  });
}
